// PAP Analytics — pure per-date + rollup computation.
//
// Data model:
//   - Only one dedicated PAP product code exists: 'PAP' (continuous_mirror,
//     standalone pap only sales). No per-size PAP SKUs — Full/Half/Plate
//     PAP amounts are derived from MEAL sales × papConfig[size], matching
//     the DailyOps PAP Contribution card.
//   - papWarmers is stored on the per-date meta doc under morningLogs
//     and read via getPapWarmers(dept, date).
//
// The computation is pure: pass sales + warmers + papConfig, get back a
// structured result. No React, no Firestore calls — so it's trivial to
// unit-test and reuse from any surface (Insights tab, CSV export, future
// scheduled reports).

import { isMealSale } from '../utils/reconciliationEngine'

// The standalone PAP product code. Meal-derived PAP contributions are
// matched by category='pap only' OR by the product's own saleType chain.
export const PAP_PRODUCT_CODES = ['PAP']

/** Classify a meal product's PAP portion as full / half / plate.
 *  Default 'plate' when the name doesn't contain full/half/hafu. */
function papSizeFor(product) {
  const n = String(product?.name || '').toLowerCase()
  if (n.includes('full')) return 'full'
  if (n.includes('half') || n.includes('hafu')) return 'half'
  return 'plate'
}

/** For one date's sales + warmers, return the per-day PAP row. */
function computeDayRow(date, sales, warmers, productByCode, papConfig) {
  let papOnly = 0
  let fullPap = 0
  let halfPap = 0
  let platePap = 0

  for (const s of (sales || [])) {
    const code = String(s.productCode || '').toUpperCase()
    const prod = productByCode.get(code)
    const qty = Number(s.qtySold) || 0
    const rands = Number(s.salesRands) || 0

    // Standalone PAP — rand passthrough from POS.
    if (PAP_PRODUCT_CODES.includes(code)) {
      papOnly += rands
      continue
    }

    // Meal contributions: qty × papConfig[size]. Only meal sales count
    // (isMealSale === true) — non-meal products don't carry PAP.
    if (!prod) continue
    if (prod.saleType === 'continuous_mirror') continue
    if (!isMealSale(prod.name, prod.code)) continue

    const size = papSizeFor(prod)
    const cost = Number(papConfig?.[size]) || 0
    const contribution = qty * cost
    if (size === 'full') fullPap += contribution
    else if (size === 'half') halfPap += contribution
    else platePap += contribution
  }

  const totalRevenue = Math.round((papOnly + fullPap + halfPap + platePap) * 100) / 100
  const warmerCount = warmers == null ? null : Number(warmers)
  const revenuePerWarmer = warmerCount != null && warmerCount > 0
    ? Math.round((totalRevenue / warmerCount) * 100) / 100
    : null

  return {
    date,
    warmers: warmerCount,
    papOnly: Math.round(papOnly * 100) / 100,
    fullPap: Math.round(fullPap * 100) / 100,
    halfPap: Math.round(halfPap * 100) / 100,
    platePap: Math.round(platePap * 100) / 100,
    totalRevenue,
    revenuePerWarmer,
  }
}

/** Compute PAP analytics across a set of pre-fetched per-date inputs.
 *
 *  @param {Array} dayInputs  [{ date, sales[], warmers|null }]
 *                            sales array may be empty; warmers null means
 *                            the staff didn't record warmer count that day
 *  @param {Array} products   list of product objects (for code/name/saleType)
 *  @param {Object} papConfig { full, half, plate } cost per serving
 *  @returns {{ days:Array, summary:Object }}
 */
export function computePapAnalytics(dayInputs, products, papConfig) {
  const productByCode = new Map()
  for (const p of (products || [])) {
    if (p?.code) productByCode.set(String(p.code).toUpperCase(), p)
  }

  // Sort by date so the causal rolling average has a stable "prior days"
  // definition — expected for day N is always warmers[N] × avg(prior
  // valid days' R/warmer). First valid day has no history → null.
  const ordered = [...(dayInputs || [])]
    .map(({ date, sales, warmers }) =>
      computeDayRow(date, sales, warmers, productByCode, papConfig || {})
    )
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))

  // Walk forward, compute causal rolling average. A "valid" day has both
  // warmers > 0 and totalRevenue > 0 — a zero-warmer or zero-revenue day
  // contributes no signal and is skipped for the rolling calc.
  let priorSum = 0
  let priorCount = 0
  for (const d of ordered) {
    const isValid = (d.warmers || 0) > 0 && (d.totalRevenue || 0) > 0
    if (priorCount > 0 && d.warmers != null && d.warmers > 0) {
      const rollingAvg = priorSum / priorCount
      d.expected = Math.round(d.warmers * rollingAvg * 100) / 100
      d.variance = Math.round((d.totalRevenue - d.expected) * 100) / 100
    } else {
      d.expected = null
      d.variance = null
    }
    if (isValid) {
      priorSum += d.revenuePerWarmer
      priorCount += 1
    }
  }

  const days = ordered

  // Full-period average for the headline KPI card — separate from the
  // causal rolling used for per-day expected.
  const validRows = days.filter(d => (d.warmers || 0) > 0 && (d.totalRevenue || 0) > 0)
  const avgRevenuePerWarmer = validRows.length > 0
    ? Math.round(
        (validRows.reduce((s, d) => s + d.revenuePerWarmer, 0) / validRows.length) * 100
      ) / 100
    : 0

  const totalRevenue = Math.round(days.reduce((s, d) => s + (d.totalRevenue || 0), 0) * 100) / 100
  const totalWarmers = days.reduce((s, d) => s + (d.warmers || 0), 0)
  const daysRecorded = days.filter(d => d.warmers != null).length

  // Shortfall rollup: days where actual < expected by > R0.01. Track the
  // biggest shortfall for the dashboard KPI.
  let daysWithShortfall = 0
  let biggestShortfall = null
  for (const d of days) {
    if (d.variance != null && d.variance < -0.01) {
      daysWithShortfall++
      if (!biggestShortfall || d.variance < biggestShortfall.amount) {
        biggestShortfall = { date: d.date, amount: d.variance }
      }
    }
  }

  return {
    days,
    summary: {
      avgRevenuePerWarmer,
      totalRevenue,
      totalWarmers,
      daysRecorded,
      daysWithShortfall,
      biggestShortfall,
    },
  }
}

/** Export helper: serialise the days array to CSV. */
export function papDaysToCsv(days) {
  const header = ['Date', 'Warmers', 'PAP Only', 'Full', 'Half', 'Plate', 'Total Revenue', 'R/Warmer', 'Expected', 'Variance']
  const rows = (days || []).map(d => [
    d.date,
    d.warmers == null ? '' : d.warmers,
    d.papOnly.toFixed(2),
    d.fullPap.toFixed(2),
    d.halfPap.toFixed(2),
    d.platePap.toFixed(2),
    d.totalRevenue.toFixed(2),
    d.revenuePerWarmer == null ? '' : d.revenuePerWarmer.toFixed(2),
    d.expected == null ? '' : d.expected.toFixed(2),
    d.variance == null ? '' : d.variance.toFixed(2),
  ])
  return [header, ...rows].map(r => r.map(cell => {
    const s = String(cell ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }).join(',')).join('\n')
}
